import { writable } from 'svelte/store';
import { apiFetchAuthed } from '../api';
import type { TextOutreach } from '../outreach';

export interface VoterSummary {
  id: string;
  voter_reg_num: string;
  first_name: string | null;
  last_name: string | null;
  address: string | null;
  city: string | null;
  zip: string | null;
  county: string | null;
  precinct: string | null;
  party: string | null;
  age: number | null;
  phone: string | null;
  email: string | null;
  do_not_contact: boolean;
  lat: number | null;
  lng: number | null;
  latest_support_level: number | null;
  latest_contact_result: string | null;
  latest_contact_at: string | null;
  newly_registered?: boolean;
  on_target_list?: boolean;
  petition_signed?: boolean;
  has_donated?: boolean;
  has_volunteered?: boolean;
  text_outreach?: TextOutreach | null;
  has_voted?: boolean;
  ballot_method?: 'early' | 'mail' | null;
  ballot_returned_on?: string | null;
  ballot_status?: string | null;
  manually_targeted?: boolean;
  manually_excluded?: boolean;
}

// Lean shape for map rendering + client-side filtering. ~6× smaller than
// VoterSummary because it omits name, address, phone, email, etc. Click a
// marker → fetch full VoterDetail via /api/voters/{id} for the popup.
export interface VoterMapPoint {
  id: string;
  lat: number | null;
  lng: number | null;
  do_not_contact: boolean;
  latest_support_level: number | null;
  latest_contact_result: string | null;
  latest_contact_at: string | null;   // NEW
  county: string | null;
  party: string | null;
  age: number | null;
  first_name: string | null;
  last_name: string | null;
  latest_contact_by_user_id: string | null;
  newly_registered?: boolean;
  petition_signed?: boolean;
  has_donated?: boolean;
  has_volunteered?: boolean;
  text_outreach?: TextOutreach | null;
  has_voted?: boolean;
}

export const votersStore = writable<VoterSummary[]>([]);
export const mapPointsStore = writable<VoterMapPoint[]>([]);

export async function fetchAllVoters(): Promise<void> {
  // Full voter records (heavy). Kept for List / MyContacts pages that need
  // names + addresses. Map uses fetchMapPoints() instead.
  const res = await apiFetchAuthed(`/api/voters?limit=30000`);
  if (!res.ok) throw new Error(`GET /api/voters failed: ${res.status}`);
  const data = await res.json();
  votersStore.set(data.items);
}

export async function fetchMapPoints(opts: { audience?: 'targeted' | 'all' } = {}): Promise<void> {
  const qs = opts.audience === 'all' ? '?audience=all' : '';
  const res = await apiFetchAuthed(`/api/voters/map${qs}`);
  if (!res.ok) throw new Error(`GET /api/voters/map failed: ${res.status}`);
  const data = await res.json();
  mapPointsStore.set(data.items);
  saveCachedMapPoints(data.items);
}

// ── localStorage cache (desktop/tablet only) ─────────────────────────────────
//
// Phones skip the cache because:
//   1) localStorage quotas are tighter on mobile browsers, and ~24k voters
//      serialized is right at the 5-10 MB ceiling.
//   2) Mobile users tend to be in the field for short, focused sessions where
//      "always fetch fresh" is the more honest behavior.
// Tablets get caching — they're often the primary device for canvass clipboards.

// IndexedDB-backed cache. Used to be localStorage but the ~6 MB voter
// payload exceeded the typical 5 MB origin quota and writes silently
// failed. IndexedDB has much higher limits (tens to hundreds of MB).

const DB_NAME = 'canvass';
const STORE = 'cache';
// Bumped to v4 with text_outreach: a cached payload written by an earlier
// image has no outreach state, and painting it would show the whole district
// as "No text data" on first paint. Bumped to v5 with has_voted, for the same
// reason: a v4 payload would paint every voter as not yet voted.
const MAP_POINTS_CACHE_VERSION = 5;
export const MAP_POINTS_CACHE_KEY = `mappoints.v${MAP_POINTS_CACHE_VERSION}`;

// Every cache key this app has shipped before the current one. Derived from
// MAP_POINTS_CACHE_VERSION, not retyped, so the next version bump retires the
// key it replaces automatically instead of relying on someone to remember to
// list it here. These are deleted (never read) below so a superseded payload
// of first/last name, party, age and coordinates for ~24k voters does not sit
// indefinitely in a shared device's IndexedDB.
export const LEGACY_MAP_POINTS_CACHE_KEYS: string[] = Array.from(
  { length: MAP_POINTS_CACHE_VERSION - 1 },
  (_, i) => `mappoints.v${i + 1}`,
);

export interface CachedMapPoints {
  points: VoterMapPoint[];
  saved_at: string;
}

export function isLocalStorageEligible(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  if (typeof indexedDB === 'undefined') return false;
  // Coarse pointer + narrow width = phone. Tablets and desktops opt in.
  const isPhone = window.matchMedia('(pointer: coarse) and (max-width: 768px)').matches;
  return !isPhone;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function loadCachedMapPoints(): Promise<CachedMapPoints | null> {
  if (!isLocalStorageEligible()) return null;
  let result: CachedMapPoints | null = null;
  try {
    const db = await openDB();
    result = await new Promise<CachedMapPoints | null>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(MAP_POINTS_CACHE_KEY);
      req.onsuccess = () => {
        const val = req.result as CachedMapPoints | undefined;
        resolve(val && Array.isArray(val.points) ? val : null);
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    result = null;
  }
  // Cleanup of caches from earlier shipped versions, now that this device
  // has read (or tried to read) the current one. Runs on every cached load,
  // not once: delete is a no-op on an absent key, so repeating it costs
  // nothing. Started here but not awaited, so a slow or hung cleanup
  // transaction never delays the cached first paint this function exists to
  // serve; any error is swallowed inside deleteCacheKeys and nothing is
  // logged.
  void deleteCacheKeys(LEGACY_MAP_POINTS_CACHE_KEYS);
  return result;
}

// Deletes the given IndexedDB cache keys in one transaction. A missing store
// or a missing key is not an error (delete is a no-op on a key that is not
// present); no key or value is ever logged.
async function deleteCacheKeys(keys: string[]): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      for (const key of keys) tx.objectStore(STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // ignore: no IndexedDB support, no cache store yet, or nothing to delete.
  }
}

export async function saveCachedMapPoints(points: VoterMapPoint[]): Promise<void> {
  if (!isLocalStorageEligible()) return;
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(
        { points, saved_at: new Date().toISOString() },
        MAP_POINTS_CACHE_KEY,
      );
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // ignore — silent cache write failure is benign; we just fetch fresh next time.
  }
}

export async function clearCachedVoters(): Promise<void> {
  // Clear in-memory state too — otherwise the next user on a shared device
  // briefly sees the previous user's voter set on first paint.
  votersStore.set([]);
  mapPointsStore.set([]);
  // Remove any old localStorage entries from earlier attempts.
  try { localStorage.removeItem('canvass.voters.v1'); } catch {}
  try { localStorage.removeItem('canvass.mappoints.v1'); } catch {}
  // Clear the IndexedDB cache: the current key and every superseded one, so a
  // shared device does not keep the previous user's cached voter data around
  // under an old key that nothing else ever reads again.
  await deleteCacheKeys([MAP_POINTS_CACHE_KEY, ...LEGACY_MAP_POINTS_CACHE_KEYS]);
}

export function updateVoterLocally(id: string, patch: Partial<VoterSummary>): void {
  votersStore.update(voters =>
    voters.map(v => (v.id === id ? { ...v, ...patch } : v))
  );
}
